import { useEffect, useState, useCallback } from "react";
import { api, API, apiError } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Loader2, Download, Check, RefreshCw, Trash2 } from "lucide-react";

const FILTERS = [
  { v: "pending", label: "Pending Print" },
  { v: "awaiting_details", label: "Awaiting Details" },
  { v: "completed", label: "Completed" },
  { v: "all", label: "All" },
];

export default function AdminConsole() {
  const [filter, setFilter] = useState("pending");
  const [jobs, setJobs] = useState(null);
  const [stats, setStats] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setJobs(null);
    const [j, s] = await Promise.all([
      api.get(`/admin/jobs?status=${filter}`),
      api.get("/admin/stats"),
    ]);
    setJobs(j.data.jobs);
    setStats(s.data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const download = (id) => {
    const token = localStorage.getItem("bag_token");
    window.open(`${API}/jobs/${id}/download?auth=${token}`, "_blank");
  };

  const complete = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/jobs/${id}/complete`);
      toast.success("Job marked complete — customer notified");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id, num) => {
    if (!window.confirm(`Delete job ${num}? This permanently removes it and cannot be undone.`)) return;
    setBusy(id);
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success(`Job ${num} deleted`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const metrics = [
    { label: "Pending", value: stats.pending ?? 0, color: "text-amber-600" },
    { label: "Awaiting details", value: stats.awaiting ?? 0, color: "text-slate-600" },
    { label: "Completed", value: stats.completed ?? 0, color: "text-green-600" },
    { label: "Total jobs", value: stats.total ?? 0, color: "text-brand" },
  ];

  return (
    <AdminLayout>
      <div className="p-6 md:p-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">Management console</span>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink mt-2">Print jobs</h1>
          </div>
          <button onClick={load} data-testid="refresh-jobs" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-ink border border-slate-200 bg-white px-4 py-2.5 rounded-lg transition-colors">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {metrics.map((m) => (
            <div key={m.label} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`stat-${m.label}`}>
              <div className="uppercase text-xs tracking-[0.15em] text-slate-500">{m.label}</div>
              <div className={`font-heading font-black text-3xl mt-1 ${m.color}`}>{m.value}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-8">
          {FILTERS.map((f) => (
            <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                filter === f.v ? "bg-ink text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
              }`}>{f.label}</button>
          ))}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
          {jobs === null ? (
            <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" size={28} /></div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-20 text-slate-500" data-testid="no-admin-jobs">No jobs in this view.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-jobs-table">
                <thead>
                  <tr className="text-left uppercase text-xs tracking-[0.12em] text-slate-500 border-b border-slate-200">
                    <th className="px-5 py-3 font-medium">Submitted</th>
                    <th className="px-5 py-3 font-medium">Job #</th>
                    <th className="px-5 py-3 font-medium">Document</th>
                    <th className="px-5 py-3 font-medium">Customer</th>
                    <th className="px-5 py-3 font-medium">Options</th>
                    <th className="px-5 py-3 font-medium">Price</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`job-row-${j.job_number}`}>
                      <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(j.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4 font-mono text-brand font-semibold whitespace-nowrap">{j.job_number}</td>
                      <td className="px-5 py-4 max-w-[220px]">
                        <div className="font-semibold text-ink truncate">{j.title}</div>
                        {j.notes && <div className="text-xs text-amber-700 mt-1 whitespace-pre-wrap" data-testid={`admin-notes-${j.job_number}`}><span className="font-semibold">Note:</span> {j.notes}</div>}
                      </td>
                      <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{j.customer_email}</td>
                      <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{j.size} · {j.color === "color" ? "Colour" : "Mono"} · {j.paper}gsm · {j.sided} · ×{j.copies}</td>
                      <td className="px-5 py-4 font-semibold text-ink">€{Number(j.price).toFixed(2)}</td>
                      <td className="px-5 py-4"><StatusBadge status={j.status} /></td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {j.storage_path && (
                            <button onClick={() => download(j.id)} data-testid={`admin-download-${j.job_number}`} title="Download file"
                              className="grid place-items-center w-9 h-9 rounded-lg border border-slate-200 text-slate-600 hover:border-brand hover:text-brand transition-colors">
                              <Download size={16} />
                            </button>
                          )}
                          {j.status !== "completed" && j.status !== "awaiting_details" && (
                            <button onClick={() => complete(j.id)} disabled={busy === j.id} data-testid={`complete-${j.job_number}`}
                              className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors">
                              {busy === j.id ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Complete
                            </button>
                          )}
                          <button onClick={() => remove(j.id, j.job_number)} disabled={busy === j.id} data-testid={`delete-${j.job_number}`} title="Delete job"
                            className="grid place-items-center w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:border-red-400 hover:text-red-500 transition-colors disabled:opacity-40">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
